import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminApi } from '../services/adminApi';
import { adsApi, bannerApi, BannerPosition } from '../services/api';
import type { AdListItem, Banner } from '../services/api';
import { useQuery } from '@tanstack/react-query';
import { 
  MagnifyingGlassIcon, 
  UserIcon, 
  HeartIcon, 
  PlusIcon,
  Bars3Icon,
  XMarkIcon,
  CogIcon,
  ClockIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

// Search history interface
interface SearchHistoryItem {
  id: string;
  query: string;
  timestamp: number;
}

// Enhanced Search Component
const EnhancedSearch: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load search history from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('searchHistory');
    if (saved) {
      try {
        setSearchHistory(JSON.parse(saved));
      } catch (error) {
        console.error('Error loading search history:', error);
      }
    }
  }, []);

  // Save search history to localStorage
  const saveSearchHistory = (history: SearchHistoryItem[]) => {
    localStorage.setItem('searchHistory', JSON.stringify(history));
    setSearchHistory(history);
  };

  // Add search to history
  const addToHistory = (query: string) => {
    if (query.trim()) {
      const newItem: SearchHistoryItem = {
        id: Date.now().toString(),
        query: query.trim(),
        timestamp: Date.now()
      };
      
      const updatedHistory = [
        newItem,
        ...searchHistory.filter(item => item.query !== query.trim())
      ].slice(0, 10); // Keep only 10 most recent
      
      saveSearchHistory(updatedHistory);
    }
  };

  // Remove item from history
  const removeFromHistory = (id: string) => {
    const updatedHistory = searchHistory.filter(item => item.id !== id);
    saveSearchHistory(updatedHistory);
  };

  // Clear all history
  const clearHistory = () => {
    saveSearchHistory([]);
  };

  // Fetch real-time search results
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['searchSuggestions', searchTerm],
    queryFn: () => adsApi.search({ searchTerm, pageSize: 5 }),
    enabled: searchTerm.length > 2 && isDropdownOpen,
    staleTime: 30000, // 30 seconds
  });

  // Fetch banner for search dropdown
  const { data: bannerData, error: bannerError, isLoading: bannerLoading } = useQuery({
    queryKey: ['searchDropdownBanner'],
    queryFn: () => bannerApi.getByPosition(BannerPosition.SearchDropdown),
    staleTime: 300000, // 5 minutes
  });

  // State for banner carousel
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const banners = bannerData?.data || [];
  const banner = banners[currentBannerIndex];

  // Reset banner index when banners data changes
  useEffect(() => {
    if (banners.length > 0 && currentBannerIndex >= banners.length) {
      setCurrentBannerIndex(0);
    }
  }, [banners.length, currentBannerIndex]);

  // Auto-rotate banners every 5 seconds if multiple banners exist
  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentBannerIndex((prevIndex) => (prevIndex + 1) % banners.length);
      }, 5000); // 5 seconds

      return () => clearInterval(interval);
    }
  }, [banners.length]);
  


  // Handle search submission
  const handleSearch = (query: string = searchTerm) => {
    if (query.trim()) {
      addToHistory(query.trim());
      navigate(`/search?search=${encodeURIComponent(query.trim())}`);
      setSearchTerm('');
      setIsDropdownOpen(false);
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Search Input */}
      <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }} className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsDropdownOpen(true)}
          placeholder="جستجو برای هر چیزی..."
          className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
          dir="rtl"
        />
        <button
          type="submit"
          className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <MagnifyingGlassIcon className="h-5 w-5" />
        </button>
      </form>

      {/* Search Dropdown */}
      {isDropdownOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          
          {/* Banner Section */}
          {banner && (
            <div className="p-2 border-b border-gray-100">
              <div className="relative overflow-hidden rounded-lg shadow-lg">
                {banner.linkUrl ? (
                  <a
                    href={banner.linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group"
                  >
                    <div className="relative w-full">
                      <img
                        src={banner.imageUrl}
                        alt={banner.title}
                        className="w-full h-auto object-cover group-hover:scale-[1.02] transition-transform duration-300 rounded-lg"
                        style={{ maxHeight: '200px', aspectRatio: '800/250' }}
                      />
                      {/* Banner navigation dots */}
                      {banners.length > 1 && (
                        <div className="absolute top-2 right-2 flex space-x-1">
                          {banners.map((_, index) => (
                            <button
                              key={index}
                              onClick={(e) => {
                                e.preventDefault();
                                setCurrentBannerIndex(index);
                              }}
                              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                                index === currentBannerIndex 
                                  ? 'bg-white shadow-lg' 
                                  : 'bg-white/50 hover:bg-white/75'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    {banner.title && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 rounded-b-lg">
                        <p className="text-white text-sm font-medium text-center">{banner.title}</p>
                      </div>
                    )}
                  </a>
                ) : (
                  <div className="relative">
                    <div className="relative w-full">
                      <img
                        src={banner.imageUrl}
                        alt={banner.title}
                        className="w-full h-auto object-cover rounded-lg"
                        style={{ maxHeight: '200px', aspectRatio: '800/250' }}
                      />
                      {/* Banner navigation dots */}
                      {banners.length > 1 && (
                        <div className="absolute top-2 right-2 flex space-x-1">
                          {banners.map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentBannerIndex(index)}
                              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                                index === currentBannerIndex 
                                  ? 'bg-white shadow-lg' 
                                  : 'bg-white/50 hover:bg-white/75'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    {banner.title && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 rounded-b-lg">
                        <p className="text-white text-sm font-medium text-center">{banner.title}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          


          {/* Real-time Search Results */}
          {searchTerm.length > 2 && (
            <div className="border-b border-gray-100">
              {isSearching ? (
                <div className="p-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">در حال جستجو...</p>
                </div>
              ) : searchResults?.data?.ads && searchResults.data.ads.length > 0 ? (
                <div className="p-2">
                  <h3 className="text-sm font-medium text-gray-700 mb-2 px-2">نتایج جستجو</h3>
                  {searchResults.data.ads.slice(0, 5).map((ad: AdListItem) => (
                    <button
                      key={ad.id}
                      onClick={() => {
                        navigate(`/ads/${ad.slug || ad.id}`);
                        setIsDropdownOpen(false);
                      }}
                      className="w-full flex items-center p-2 hover:bg-gray-50 rounded text-right"
                      dir="rtl"
                    >
                      {ad.primaryImageUrl && (
                        <img
                          src={ad.primaryImageUrl}
                          alt={ad.title}
                          className="w-12 h-12 object-cover rounded ml-3 flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{ad.title}</p>
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <span>${ad.price.toLocaleString()}</span>
                          <span className="mx-1">•</span>
                          <span>{ad.city}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                  <button
                    onClick={() => handleSearch()}
                    className="w-full p-2 text-sm text-blue-600 hover:bg-blue-50 rounded mt-1"
                  >
                    مشاهده همه نتایج ({searchResults?.data?.totalCount || 0})
                  </button>
                </div>
              ) : searchTerm.length > 2 ? (
                <div className="p-4 text-center">
                  <p className="text-sm text-gray-500">نتیجه‌ای یافت نشد</p>
                  <button
                    onClick={() => handleSearch()}
                    className="text-sm text-blue-600 hover:text-blue-700 mt-1"
                  >
                    جستجو در همه آگهی‌ها
                  </button>
                </div>
              ) : null}
            </div>
          )}

          {/* Search History */}
          {searchHistory.length > 0 && (
            <div className="p-2">
              <div className="flex items-center justify-between mb-2 px-2">
                <h3 className="text-sm font-medium text-gray-700">جستجوهای اخیر</h3>
                <button
                  onClick={clearHistory}
                  className="text-xs text-gray-500 hover:text-red-600"
                >
                  پاک کردن همه
                </button>
              </div>
              {searchHistory.slice(0, 5).map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-2 hover:bg-gray-50 rounded group"
                >
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSearchTerm(item.query);
                      handleSearch(item.query);
                    }}
                    className="flex items-center flex-1 text-right"
                    dir="rtl"
                  >
                    <ClockIcon className="h-4 w-4 text-gray-400 ml-2 flex-shrink-0" />
                    <span className="text-sm text-gray-700 truncate">{item.query}</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeFromHistory(item.id);
                    }}
                    className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <TrashIcon className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {searchTerm.length <= 2 && searchHistory.length === 0 && (
            <div className="p-4 text-center">
              <MagnifyingGlassIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">شروع به تایپ کنید تا نتایج را ببینید</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function Header() {
  const { user, isAuthenticated, isInitialized, isAdminAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleAdminLogout = async () => {
    try {
      await logout();
      navigate('/admin/login');
    } catch (error) {
      console.error('Admin logout error:', error);
      navigate('/admin/login');
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.dropdown-container')) {
        setIsAdminDropdownOpen(false);
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdowns when route changes
  useEffect(() => {
    setIsAdminDropdownOpen(false);
    setIsUserDropdownOpen(false);
  }, [location.pathname]);

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="/images/logo.png" 
              alt="timcheh" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Enhanced Search Bar */}
          <div className="flex-1 max-w-lg mx-4 sm:mx-8">
            <EnhancedSearch />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {isAdminAuthenticated ? (
              <>
                <div className="relative dropdown-container">
                  <button 
                    onClick={() => setIsAdminDropdownOpen(!isAdminDropdownOpen)}
                    className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    <UserIcon className="h-5 w-5" />
                  </button>
                  
                  {/* Admin Dropdown Menu */}
                  <div className={`absolute left-0 right-0 sm:right-0 sm:left-auto mt-2 w-48 sm:w-52 bg-white rounded-lg shadow-xl border border-gray-200 py-2 transition-all duration-200 z-50 transform origin-top-right ${
                    isAdminDropdownOpen ? 'opacity-100 visible scale-100' : 'opacity-0 invisible scale-95'
                  }`} dir="rtl">
                    <Link
                      to="/admin/dashboard"
                      onClick={() => setIsAdminDropdownOpen(false)}
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <svg className="h-4 w-4 ml-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      داشبورد
                    </Link>
                    <Link
                      to="/admin/users"
                      onClick={() => setIsAdminDropdownOpen(false)}
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <svg className="h-4 w-4 ml-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                      کاربران
                    </Link>
                    <Link
                      to="/admin/ads"
                      onClick={() => setIsAdminDropdownOpen(false)}
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <svg className="h-4 w-4 ml-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                      </svg>
                      آگهی‌ها
                    </Link>
                    <Link
                      to="/admin/categories"
                      onClick={() => setIsAdminDropdownOpen(false)}
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <svg className="h-4 w-4 ml-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-4H3m16 8H7m10 4H5" />
                      </svg>
                      دسته‌بندی‌ها
                    </Link>
                    <div className="border-t border-gray-100 my-1"></div>
                    <button
                      onClick={() => {
                        handleAdminLogout();
                        setIsAdminDropdownOpen(false);
                      }}
                      className="flex items-center w-full text-right px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                    >
                      <svg className="h-4 w-4 ml-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      خروج مدیر
                    </button>
                  </div>
                </div>
              </>
            ) : !isInitialized ? (
              // Show loading state during auth check
              <div className="flex items-center space-x-4">
                <div className="h-8 w-16 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
              </div>
            ) : isAuthenticated ? (
              <>
                <Link
                  to="/post-ad"
                  className="text-white px-6 py-3 ml-8 rounded flex items-center space-x-2 font-medium shadow-lg transition-all duration-200 hover:shadow-xl"
                  style={{ 
                    backgroundColor: 'rgb(170, 0, 1)' as const
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgb(140, 0, 1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgb(170, 0, 1)';
                  }}
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>ثبت آگهی</span>
                </Link>
                
                

                <div className="relative ml-12 dropdown-container">
                  <button 
                    onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                    className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    <UserIcon className="h-5 w-5" />
                  </button>
                  
                  {/* Dropdown Menu */}
                  <div className={`absolute left-0 right-0 sm:right-0 sm:left-auto mt-2 w-56 sm:w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 transition-all duration-200 z-50 transform origin-top-right ${
                    isUserDropdownOpen ? 'opacity-100 visible scale-100' : 'opacity-0 invisible scale-95'
                  }`} dir="rtl">
                    <Link
                      to="/profile?tab=profile"
                      onClick={() => setIsUserDropdownOpen(false)}
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <UserIcon className="h-4 w-4 ml-3 text-gray-400" />
                      پروفایل من
                    </Link>
                    {user?.userType === 'Business' && (
                      <Link
                        to={`/business/${user.businessSlug || 'business'}`}
                        onClick={() => setIsUserDropdownOpen(false)}
                        className="flex items-center px-4 py-2.5 text-sm text-purple-600 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                      >
                        <svg className="h-4 w-4 ml-3 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        فروشگاه من
                      </Link>
                    )}
                    <Link
                      to="/my-ads"
                      onClick={() => setIsUserDropdownOpen(false)}
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <svg className="h-4 w-4 ml-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                      </svg>
                      آگهی‌های من
                    </Link>
                    <Link
                      to="/favorites?tab=favorites"
                      onClick={() => setIsUserDropdownOpen(false)}
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <HeartIcon className="h-4 w-4 ml-3 text-gray-400" />
                      علاقه‌مندی‌ها
                    </Link>
                    <Link
                      to="/my-ads?tab=plans"
                      onClick={() => setIsUserDropdownOpen(false)}
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <svg className="h-4 w-4 ml-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      پلن‌های من
                    </Link>
                    <Link
                      to="/my-ads?tab=payments"
                      onClick={() => setIsUserDropdownOpen(false)}
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <svg className="h-4 w-4 ml-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      پرداخت‌ها
                    </Link>
                    <Link
                      to="/my-ads?tab=settings"
                      onClick={() => setIsUserDropdownOpen(false)}
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <CogIcon className="h-4 w-4 ml-3 text-gray-400" />
                      تنظیمات
                    </Link>
                    <div className="border-t border-gray-100 my-1"></div>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsUserDropdownOpen(false);
                      }}
                      className="flex items-center w-full text-right px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                    >
                      <svg className="h-4 w-4 ml-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      خروج
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2"
                >
                  ورود
                </Link>
                <Link
                  to="/register"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  ثبت‌نام
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-600 hover:text-gray-900"
            >
              {isMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {isAdminAuthenticated ? (
              <>
                <Link
                  to="/admin/dashboard"
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  onClick={() => setIsMenuOpen(false)}
                >
                  داشبورد
                </Link>
                <Link
                  to="/admin/users"
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  onClick={() => setIsMenuOpen(false)}
                >
                  کاربران
                </Link>
                <Link
                  to="/admin/ads"
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  onClick={() => setIsMenuOpen(false)}
                >
                  آگهی‌ها
                </Link>
                <Link
                  to="/admin/categories"
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  onClick={() => setIsMenuOpen(false)}
                >
                  دسته‌بندی‌ها
                </Link>
                <button
                  onClick={() => {
                    handleAdminLogout();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-right px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                >
                  خروج مدیر
                </button>
              </>
            ) : !isInitialized ? (
              // Show loading state during auth check
              <div className="flex justify-center items-center py-4">
                <div className="h-6 w-32 bg-gray-200 rounded animate-pulse"></div>
              </div>
            ) : isAuthenticated ? (
              <div className="space-y-1" dir="rtl">
                <Link
                  to="/post-ad"
                  className="block mx-3 px-4 py-3 text-base font-medium text-white rounded text-center shadow-lg transition-all duration-200"
                  style={{ 
                    backgroundColor: 'rgb(170, 0, 1)' as const
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgb(140, 0, 1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgb(170, 0, 1)';
                  }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  + ثبت آگهی
                </Link>
                <Link
                  to="/profile?tab=profile"
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  onClick={() => setIsMenuOpen(false)}
                >
                  پروفایل من
                </Link>
                {user?.userType === 'Business' && user?.businessApprovalStatus === 1 && (
                  <Link
                    to={`/business/${user.businessSlug || 'business'}`}
                    className="block px-3 py-2 text-base font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    🏢 فروشگاه من
                  </Link>
                )}
                <Link
                  to="/my-ads"
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  onClick={() => setIsMenuOpen(false)}
                >
                  آگهی‌های من
                </Link>
                <Link
                  to="/favorites?tab=favorites"
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  onClick={() => setIsMenuOpen(false)}
                >
                  علاقه‌مندی‌ها
                </Link>
                <Link
                  to="/my-ads?tab=plans"
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  onClick={() => setIsMenuOpen(false)}
                >
                  پلن‌های من
                </Link>
                <Link
                  to="/my-ads?tab=payments"
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  onClick={() => setIsMenuOpen(false)}
                >
                  پرداخت‌ها
                </Link>
                <Link
                  to="/my-ads?tab=settings"
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  onClick={() => setIsMenuOpen(false)}
                >
                  تنظیمات
                </Link>
                <div className="border-t border-gray-200 my-2"></div>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-right px-3 py-2 text-base font-medium text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  خروج
                </button>
              </div>
            ) : (
              <div className="space-y-1" dir="rtl">
                <Link
                  to="/login"
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  onClick={() => setIsMenuOpen(false)}
                >
                  ورود
                </Link>
                <Link
                  to="/register"
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  onClick={() => setIsMenuOpen(false)}
                >
                  ثبت‌نام
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
} 